import _ from 'lodash';
import { ELEMENT_LI, ELEMENT_OL, ELEMENT_UL } from '@udecode/plate-list';
import {
  ELEMENT_IMAGE,
  ELEMENT_LINK,
  ELEMENT_PARAGRAPH,
  ELEMENT_TABLE,
  ELEMENT_TR,
  ELEMENT_TD,
  ELEMENT_TH,
} from '@udecode/plate';
import escapeHtml from 'escape-html';
import { KEY_ALIGN } from '@udecode/plate-alignment';
import { Text } from 'slate';

import { hasPluginWithKey } from 'components/text_editor_new/lib/has_plugin_with_key';
import { isValidCssColor } from 'components/text_editor_new/lib/should_allow_element_color';
import serializeHtmlText from 'components/text_editor_new/lib/serialize_html_text';

// Serializes editor content. If `options.forExternalUse` is true, will remove
// nodes like placeholders and variables that should not be leaked outside (i.e.
// in customer-facing content, such as email)
export function serializeHtml(editor, { forExternalUse = false } = {}) {
  return serializeNode(editor, editor, { forExternalUse, preserveColors: false });
}

// `options.preserveColors` keeps text colors even in external mode. It's set when we descend into a
// table cell that has an explicit background: the external color-stripping exists to stop light text
// from vanishing on a white email background, but that assumption is wrong for a shaded cell (e.g.
// white text on a dark header pasted from a spreadsheet), where we want to keep the color as-is.
function serializeNode(editor, node, options) {
  if (Text.isText(node)) {
    return serializeHtmlText(node, options);
  }

  const { forExternalUse } = options;
  const isBackgroundCell = (node.type === ELEMENT_TD || node.type === ELEMENT_TH) && !!node.background;
  const childOptions = isBackgroundCell ? { ...options, preserveColors: true } : options;
  const children = node.children.map(n => serializeNode(editor, n, childOptions)).join('');

  // Handle lists if the editor has the plugin - otherwise we'll fall through to treating
  // list items as paragraphs.
  if (hasPluginWithKey(editor, 'list')) {
    switch (node.type) {
      case ELEMENT_LI: {
        const nodeAlignment = children ? getNodeAlignment(node) : '';
        const styleString = nodeAlignment ? `style="text-align:${nodeAlignment}"` : '';
        if (styleString) {
          return `<li ${styleString}>${children}</li>`;
        }
        return `<li>${children}</li>`;
      }

      case ELEMENT_OL:
        return `<ol dir="auto">${children}</ol>`;

      case ELEMENT_UL:
        return `<ul dir="auto">${children}</ul>`;

      default:
        break;
    }
  }

  switch (node.type) {
    case ELEMENT_IMAGE: {
      const attributes = [];
      const src = _.trim(node.src);
      const attachmentId = _.trim(node.attachmentId);
      const fileName = _.trim(node.fileName);

      attributes.push(`src="${src ? encodeURI(src) : ''}"`);
      if (attachmentId) attributes.push(`data-attachment-id="${attachmentId}"`);
      if (fileName) attributes.push(`data-file="${fileName}"`);

      return `<img ${attributes.join(' ')} />`;
    }

    case ELEMENT_LINK: {
      const linkTarget = ['_blank', '_self'].includes(node.target || '') ? node.target : '_blank';
      return `<a href="${serializeHref(node.url)}" target="${linkTarget}" rel="noopener noreferrer">${children}</a>`;
    }

    case ELEMENT_TABLE:
      // border-collapse only survives in-editor (Supernova strips style on <table>); cell borders below
      // carry the visible styling in saved/rendered HTML.
      return `<table style="border-collapse:collapse">${children}</table>`;

    case ELEMENT_TR:
      return `<tr>${children}</tr>`;

    case ELEMENT_TD:
    case ELEMENT_TH: {
      const tag = node.type === ELEMENT_TH ? 'th' : 'td';
      const attributes = [];
      // Only colSpan — Plate 21 doesn't model rowSpan (see deserialize_html / the table normalizer).
      if (node.colSpan > 1) attributes.push(`colspan="${node.colSpan}"`);
      // Styling lives on cells because Supernova's sanitizer only keeps `style` on td/th, not table/tr.
      // Background (e.g. pasted shaded cells) goes first; border/padding give the table its structure.
      const styleParts = [];
      // Validate the color before interpolating it into the style attribute — a parseable CSS color
      // can't carry the characters needed to break out of `style="..."`.
      if (node.background && isValidCssColor(node.background)) styleParts.push(`background-color:${node.background}`);
      styleParts.push('border:1px solid #ccc', 'padding:4px 8px');
      attributes.push(`style="${styleParts.join(';')}"`);
      return `<${tag} ${attributes.join(' ')}>${children}</${tag}>`;
    }

    case 'placeholder':
      if (forExternalUse) return children;
      return `<placeholder>${children}</placeholder>`;

    case ELEMENT_LI:
    case ELEMENT_PARAGRAPH: {
      let renderedChildren = '<br>';
      if (children) {
        renderedChildren = children;

        // When serializing for external use, a single trailing newline / <br> in a paragraph node
        // does not actually get rendered, so we need to double up the <br> at the end to make it
        // look like a single empty newline.
        if (forExternalUse && /<br\s*\/?>$/.test(children) && children !== '<br>') {
          renderedChildren = `${renderedChildren}<br>`;
        }
      }
      const nodeAlignment = children ? getNodeAlignment(node) : '';
      return nodeAlignment
        ? `<div style="text-align:${nodeAlignment}">${renderedChildren}</div>`
        : `<div>${renderedChildren}</div>`;
    }

    case 'variable':
      if (forExternalUse) return children;
      return `<variable ${createAttributesString(node.attributes)}>${children}</variable>`;

    // Agent mentions are preserved even in external output: the saved note/comment HTML renders the
    // <agentmention> chip in the timeline and comment panel (see task_comment_panel.jsx styling).
    case 'agent-mention':
      return `<agentmention ${createAttributesString(node.attributes)}>${children}</agentmention>`;

    default:
      return children;
  }
}

// Schemes safe to emit in an href. A scheme outside this set (javascript:, data:, vbscript:, …) is
// dropped to '#'; scheme-less URLs (relative, protocol-relative, bare domains) are allowed.
const SAFE_HREF_SCHEME = /^(https?:|mailto:|tel:)/i;

// Serializes a link URL for an href attribute. Escapes it (so a crafted URL can't break out of the
// attribute and inject `on*` handlers into outbound email — the timeline re-sanitizes, but email does
// not) and drops disallowed schemes. Whitespace is stripped only for the scheme check, which defeats the
// `java\tscript:` obfuscation browsers would otherwise collapse back into a live scheme.
function serializeHref(url) {
  const trimmed = _.trim(url);
  if (!trimmed) return '';

  const forSchemeCheck = trimmed.replace(/\s+/g, '');
  const hasScheme = /^[a-z][a-z0-9+.-]*:/i.test(forSchemeCheck);
  const isSafe = !hasScheme || SAFE_HREF_SCHEME.test(forSchemeCheck);

  return escapeHtml(isSafe ? trimmed : '#');
}

function createAttributesString(attrs) {
  const strings = [];
  _.forEach(attrs, (value, key) => {
    if (value != null) {
      strings.push(`${key}="${escapeHtml(value)}"`);
    }
  });
  return strings.join(' ');
}

// Returns node "alignment" or empty string if the alignment is not defined or is set to the default (left)
function getNodeAlignment(node) {
  const alignKey = _.get(node, KEY_ALIGN);
  const alignment = _.trim(alignKey).toLowerCase();

  return alignment && alignment !== 'left' && alignment !== 'start' ? alignment : '';
}
